import { select, all, takeLatest, put, call } from 'redux-saga/effects';

import cookie from '@snapask/common-components/dist/utils/cookie';

import config from '@dragonfruit/config';
const { COOKIE_DOMAIN } = config;

import { apiFacebookSignup } from '@dragonfruit/services/apis';
import * as facebookSDK from '@dragonfruit/services/facebookSDK';

import { getQuestionId } from '@dragonfruit/utils/temporaryQuestion';

import * as actions from '@dragonfruit/redux/signup';
import * as fbAuthActions from '@dragonfruit/redux/facebookAuth';
import { goToTutorSignupPage } from '@dragonfruit/redux/navigation';
import { showAlertBanner } from '@dragonfruit/redux/alertBanner';
import { sendGtmEvent } from '@dragonfruit/redux/googleTagManager';
import { getPartnerCode } from '@dragonfruit/redux/location';

import { TUTOR } from '@dragonfruit/constants/userRoles';
import {
  COMPLETE_REGISTRATION,
  EVENT_COMPLETED_REGISTRATION,
} from '@dragonfruit/constants/gtmEvents';
import { ALERT_ERROR_THEME } from '@dragonfruit/constants/alertBannerThemes';
import { FACEBOOK } from '@dragonfruit/constants/signupAuthType';
import { oauthLoginFlow, getUserInfoByToken } from '../auth';

export function* getFacebookAuthResponse() {
  const { status, authResponse: loginStatusResponse } = yield call(
    facebookSDK.checkLoginStatus
  );
  const userIsLoggedIntoFacebook =
    status === 'connected' && loginStatusResponse;
  if (userIsLoggedIntoFacebook) return loginStatusResponse;

  const { authResponse: loginResponse } = yield call(facebookSDK.facebookLogin);
  return loginResponse;
}

export function* facebookSignupFlow(action) {
  const { userId, accessToken, countryCode } = action.payload;

  try {
    const questionId = getQuestionId();

    const snapCookie = new cookie(COOKIE_DOMAIN);
    const appsflyerConversion = snapCookie.getAppsFlyerConversionData();
    const partnerCode = yield select(getPartnerCode);

    const data = {
      provider_uid: userId,
      provider_token: accessToken,
      provider: 'facebook',
      country_code: countryCode,
      application_uid: partnerCode,
    };
    if (questionId) data.question_id = questionId;
    if (appsflyerConversion)
      data.appsflyer_conversion = JSON.stringify(appsflyerConversion);
    const facebookSignupResponse = yield call(apiFacebookSignup, data);

    const { data: { jwt: token } = {} } = facebookSignupResponse;

    yield put(actions.facebookSignupSuccess());

    yield call(getUserInfoByToken, { payload: { token } });

    yield put(
      sendGtmEvent({
        eventName: COMPLETE_REGISTRATION,
        data: {
          provider: FACEBOOK,
        },
      })
    );

    yield put(
      sendGtmEvent({
        eventName: EVENT_COMPLETED_REGISTRATION,
        data: {
          sourceType: FACEBOOK,
        },
      })
    );
  } catch (err) {
    yield put(actions.facebookSignupFailed(err.message));
    yield put(
      showAlertBanner({
        message: err.message,
        theme: ALERT_ERROR_THEME,
      })
    );
  }
}

export function* fetchFbSignupUserInfoFlow(action) {
  const { fbUserId, fbAccessToken } = action.payload;

  try {
    // fetch user facebook public profile
    const fbPublicProfile = yield call(facebookSDK.fetchFbPublicProfile);

    const { name, email } = fbPublicProfile;

    // trim all space
    const username = name.replace(/ +/g, '');

    // fetch user profile picture
    const profilePicture = yield call(facebookSDK.fetchProfilePicture);

    const { data: { url } = {} } = profilePicture;

    yield put(fbAuthActions.fetchFbSignupUserInfoSuccess());

    yield put(
      actions.storeSignupInfo({
        profilePicture: url,
        provider: FACEBOOK,
        providerUid: fbUserId,
        providerToken: fbAccessToken,
        username,
        email,
      })
    );

    yield put(actions.alterFacebookAuthType());

    yield put(goToTutorSignupPage());
  } catch (err) {
    yield put(fbAuthActions.fetchFbSignupUserInfoFailed(err.message));
  }
}

export function* checkFacebookLoginFlow(action) {
  const { countryCode } = action.payload;

  const facebookAuthResponse = yield call(getFacebookAuthResponse);
  const { userID, accessToken } = facebookAuthResponse;

  try {
    yield call(oauthLoginFlow, {
      provider: 'facebook',
      providerUid: userID,
      providerToken: accessToken,
    });
  } catch (err) {
    const signupRole = yield select(actions.getSignupRole);

    if (signupRole === TUTOR) {
      yield put(
        fbAuthActions.fetchFbSignupUserInfo({
          fbUserId: userID,
          fbAccessToken: accessToken,
        })
      );
    } else {
      yield put(
        actions.facebookSignup({
          userId: userID,
          countryCode,
          accessToken,
        })
      );
    }
  }
}

export default function*() {
  yield all([
    takeLatest(actions.FACEBOOK_SIGNUP, facebookSignupFlow),
    takeLatest(
      fbAuthActions.FETCH_FB_SIGNUP_USER_INFO,
      fetchFbSignupUserInfoFlow
    ),
    takeLatest(fbAuthActions.CHECK_FACEBOOK_LOGIN, checkFacebookLoginFlow),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/signup/facebook.js